import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { explainSplit } from '../services/planService';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import toast from 'react-hot-toast';

const TIERS = [
  { value: 0, label: 'No equipment', description: 'Bodyweight only' },
  { value: 1, label: 'Hotel gym', description: 'Dumbbells, basic machines' },
  { value: 2, label: 'Standard gym', description: 'Barbells, cables, most equipment' },
  { value: 3, label: 'Well equipped', description: 'Specialty bars, platforms, full suite' },
];

const DURATIONS = [20, 30, 45, 60, 90];
const DAYS = [1, 2, 3, 4, 5, 6];

function Setup() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tier, setTier] = useState(1);
  const [durationMin, setDurationMin] = useState(45);
  const [daysPerWeek, setDaysPerWeek] = useState(3);
  const [units, setUnits] = useState('lb');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadPrefs = async () => {
      if (!user) return;
      try {
        const userRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userRef);
        if (userDoc.exists()) {
          const prefs = userDoc.data().prefs || {};
          setTier(prefs.defaultTier ?? 1);
          setDurationMin(prefs.defaultDurationMin ?? 45);
          setDaysPerWeek(prefs.daysPerWeek ?? 3);
          setUnits(prefs.units ?? 'lb');
        }
      } catch (error) {
        console.error('Error loading preferences:', error);
      } finally {
        setLoading(false);
      }
    };
    loadPrefs();
  }, [user]);

  const handleSave = async () => {
    try {
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        prefs: {
          defaultTier: tier,
          defaultDurationMin: durationMin,
          daysPerWeek,
          units,
        },
      });
      toast.success('Preferences saved');
      navigate('/week');
    } catch (error) {
      toast.error('Error saving preferences');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-light-bg dark:bg-dark-bg">
        <div className="text-lg font-plex text-light-ink dark:text-dark-ink">Loading...</div>
      </div>
    );
  }

  const explanation = explainSplit(daysPerWeek, tier);

  return (
    <div className="min-h-screen bg-light-bg dark:bg-dark-bg px-4 py-6">
      <div className="bg-light-surface dark:bg-dark-surface rounded-[18px] p-6 space-y-6">
        <div>
          <h1 className="text-[24px] font-grotesk font-bold text-light-ink dark:text-dark-ink tracking-[-0.015em]">
            Workout Setup
          </h1>
          <p className="mt-1 text-[13px] font-plex text-light-muted dark:text-dark-muted">
            Configure your training parameters
          </p>
        </div>

        <div className="space-y-6">
          <div>
            <label className="block text-[13px] font-plex font-semibold text-light-ink dark:text-dark-ink mb-3">
              Equipment tier
            </label>
            <div className="space-y-2">
              {TIERS.map((t) => (
                <button
                  key={t.value}
                  onClick={() => setTier(t.value)}
                  className={`w-full text-left px-4 py-3 rounded-[14px] border-2 transition-colors ${
                    tier === t.value
                      ? 'border-light-accent dark:border-dark-accent bg-light-accent-soft dark:bg-dark-accent-soft'
                      : 'border-light-border dark:border-dark-border hover:border-light-ink dark:hover:border-dark-accent'
                  }`}
                >
                  <div className="font-plex font-semibold text-[15px] text-light-ink dark:text-dark-ink">
                    {t.label}
                  </div>
                  <div className="text-[13px] font-plex text-light-muted dark:text-dark-muted">
                    {t.description}
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-[13px] font-plex font-semibold text-light-ink dark:text-dark-ink mb-3">
              Session length
            </label>
            <div className="grid grid-cols-5 gap-2">
              {DURATIONS.map((d) => (
                <button
                  key={d}
                  onClick={() => setDurationMin(d)}
                  className={`h-[44px] rounded-[12px] border-2 font-plex font-semibold text-[15px] transition-colors ${
                    durationMin === d
                      ? 'border-light-accent dark:border-dark-accent bg-light-accent-soft dark:bg-dark-accent-soft text-light-ink dark:text-dark-ink'
                      : 'border-light-border dark:border-dark-border text-light-muted dark:text-dark-muted hover:border-light-ink dark:hover:border-dark-accent'
                  }`}
                >
                  {d}m
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-[13px] font-plex font-semibold text-light-ink dark:text-dark-ink mb-3">
              Days per week
            </label>
            <div className="grid grid-cols-6 gap-2">
              {DAYS.map((d) => (
                <button
                  key={d}
                  onClick={() => setDaysPerWeek(d)}
                  className={`h-[44px] rounded-[12px] border-2 font-plex font-semibold text-[15px] transition-colors ${
                    daysPerWeek === d
                      ? 'border-light-accent dark:border-dark-accent bg-light-accent-soft dark:bg-dark-accent-soft text-light-ink dark:text-dark-ink'
                      : 'border-light-border dark:border-dark-border text-light-muted dark:text-dark-muted hover:border-light-ink dark:hover:border-dark-accent'
                  }`}
                >
                  {d}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-[15px] font-plex font-bold text-light-ink dark:text-dark-ink mb-3">
              Weight units
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setUnits('lb')}
                className={`h-[52px] rounded-[14px] border-2 font-plex font-semibold text-[15px] transition-colors ${
                  units === 'lb'
                    ? 'border-light-accent dark:border-dark-accent bg-light-accent dark:bg-dark-accent text-white'
                    : 'border-light-border dark:border-dark-border text-light-ink dark:text-dark-ink hover:border-light-ink dark:hover:border-dark-accent'
                }`}
              >
                Pounds (lb)
              </button>
              <button
                onClick={() => setUnits('kg')}
                className={`h-[52px] rounded-[14px] border-2 font-plex font-semibold text-[15px] transition-colors ${
                  units === 'kg'
                    ? 'border-light-accent dark:border-dark-accent bg-light-accent dark:bg-dark-accent text-white'
                    : 'border-light-border dark:border-dark-border text-light-ink dark:text-dark-ink hover:border-light-ink dark:hover:border-dark-accent'
                }`}
              >
                Kilograms (kg)
              </button>
            </div>
          </div>

          <div className="bg-light-accent-soft dark:bg-dark-accent-soft border border-light-accent/20 dark:border-dark-accent/20 rounded-[14px] p-4">
            <h3 className="text-[13px] font-plex font-semibold text-light-ink dark:text-dark-ink mb-1">
              Your split
            </h3>
            <p className="text-[13px] font-plex text-light-muted dark:text-dark-muted">{explanation}</p>
          </div>

          <button
            onClick={handleSave}
            className="w-full h-[58px] bg-light-accent dark:bg-dark-accent text-white rounded-[17px] font-plex font-semibold text-[15px] hover:bg-light-accent-press dark:hover:bg-dark-accent transition-colors"
          >
            Generate plan
          </button>
        </div>
      </div>
    </div>
  );
}

export default Setup;
